/**
 * PATH: packages/ui/src/diagnostics/clientErrorReporter.ts
 * FILE_ID: SHARED-DIAGNOSTICS-MODULE-001
 * PURPOSE: Capture frontend errors and failed fetches for backend diagnostics.
 * WHY: Crash evidence must survive when the normal API client cannot.
 * FLOW: browser event/fetch → buffer → batched diagnostics ingest.
 * DEPENDENCIES: Browser globals only; no API client dependency.
 * RELATED: clientErrorReporterFetch.ts; clientErrorReporterGlobalListeners.ts.
 */
import { installFetchInstrumentation } from './clientErrorReporterFetch'
import {
  installClientDiagnosticsKeyboardShortcut,
  installClientErrorGlobalListeners,
  type AutomaticDiagnostic,
} from './clientErrorReporterGlobalListeners'
import type {
  ClientDiagnosticsOptions,
  DiagnosticEvent,
  DiagnosticEventType,
  FetchTiming,
} from './clientErrorReporterTypes'

export type {
  ClientDiagnosticsOptions,
  DiagnosticEvent,
  DiagnosticEventType,
  FetchTiming,
} from './clientErrorReporterTypes'

// --------- Module-level state -----------------------------------------
const RING_BUFFER_STORAGE_KEY = 'quotech_client_diagnostics_buffer'
const SESSION_KEY_STORAGE_KEY = 'quotech_client_diagnostics_session'

let installed = false
let originalFetch: typeof fetch | null = null
let opts: Required<Pick<ClientDiagnosticsOptions, 'ingestUrl' | 'maxBufferSize' | 'flushDebounceMs'>> &
  ClientDiagnosticsOptions = {
  ingestUrl: '',
  maxBufferSize: 50,
  flushDebounceMs: 2000,
}

let buffer: DiagnosticEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let sessionKey = ''
/** After a 429 from the ingest endpoint, pause flushes so we don't
 *  amplify the rate-limit storm (operator report 2026-05-21). */
let ingestPausedUntil = 0

let fetchTimingObserver: ((t: FetchTiming) => void) | null = null

/** Register an observer invoked for every completed fetch. Used by the
 *  perf reporter; pure callback, no coupling. */
export function setFetchTimingObserver(fn: ((t: FetchTiming) => void) | null): void {
  fetchTimingObserver = fn
}

/** Expose the per-tab session id so the perf reporter can correlate its
 *  samples to this tab's error/reload trail. */
export function getDiagnosticsSessionKey(): string {
  if (!sessionKey) sessionKey = _resolveSessionKey()
  return sessionKey
}

function _splitServerTimingMetrics(header: string): string[] {
  const metrics: string[] = []
  let start = 0
  let inQuotes = false
  for (let index = 0; index < header.length; index += 1) {
    if (header[index] === '"' && header[index - 1] !== '\\') inQuotes = !inQuotes
    if (header[index] === ',' && !inQuotes) {
      metrics.push(header.slice(start, index))
      start = index + 1
    }
  }
  metrics.push(header.slice(start))
  return metrics
}

function _parseServerTiming(res: Response): number | undefined {
  try {
    const header = res.headers.get('Server-Timing')
    if (!header) return undefined
    // Only the named app metric represents backend application time. A
    // preceding CDN/database metric must not be reported as app latency.
    for (const metric of _splitServerTimingMetrics(header)) {
      const [name, ...parameters] = metric.trim().split(';')
      if (name.toLowerCase() !== 'app') continue
      for (const parameter of parameters) {
        const [key, rawValue] = parameter.split('=')
        if (key.trim().toLowerCase() !== 'dur' || !rawValue) continue
        const duration = Number.parseFloat(rawValue.trim().replace(/^"|"$/g, ''))
        return Number.isFinite(duration) ? duration : undefined
      }
      return undefined
    }
  } catch {
    /* header access can throw on opaque responses */
  }
  return undefined
}

function _recordFetchResponseError({
  method,
  url,
  status,
  excerpt,
  durationMs,
}: {
  method: string
  url: string
  status: number
  excerpt: string
  durationMs: number
}): void {
  recordDiagnosticEvent({
    event_type: 'fetch_error_response',
    message: `${method} ${url} -> ${status}`,
    request_url: url,
    request_method: method,
    response_status: status,
    response_body_excerpt: excerpt,
    request_duration_ms: durationMs,
  })
}

function _isAbortLikeNetworkError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const name = (error as { name?: unknown }).name
  if (name === 'AbortError' || name === 'CancelledError') return true
  const message = (error as { message?: unknown }).message
  return (
    typeof message === 'string'
    && /signal is aborted|aborted without reason|The user aborted a request/i.test(message)
  )
}

function _recordFetchNetworkError({
  method,
  url,
  durationMs,
  error,
}: {
  method: string
  url: string
  durationMs: number
  error: unknown
}): void {
  // Intentional cancels (React Query invalidate/cancel, route unmount) are not
  // product failures. Reporting them flooded diagnostics on C384a Hartley after
  // every Time-view card create/date-patch and obscured real crashes.
  if (_isAbortLikeNetworkError(error)) return
  const message = error instanceof Error ? error.message : String(error)
  recordDiagnosticEvent({
    event_type: 'fetch_failed',
    message: `${method} ${url} threw: ${message}`,
    stack: error instanceof Error ? error.stack ?? '' : '',
    request_url: url,
    request_method: method,
    request_duration_ms: durationMs,
  })
}

function _emitFetchTiming(t: FetchTiming): void {
  if (!fetchTimingObserver) return
  try {
    fetchTimingObserver(t)
  } catch {
    /* an observer error must never break the fetch caller */
  }
}

/** Fetch failures we instrument for diagnostics — skip expected /
 *  high-churn statuses that would spam ingest and trigger 429s. */
function _shouldSkipFetchDiagnostic(status: number, url: string): boolean {
  if (status === 401) return true
  if (status === 429) return true
  if (status === 404 && url.includes('/api/jobs/scope-groups')) return true
  if (status === 400 && url.includes('/api/outlook/refresh')) return true
  return false
}

// --------- Public API -------------------------------------------------

/**
 * Install global error / fetch listeners. Safe to call multiple times.
 * The reporter is a no-op when not in a browser context (SSR, unit
 * tests).
 */
export function installClientErrorReporter(options: ClientDiagnosticsOptions = {}): void {
  if (installed) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (options.disabled) return

  const defaultIngest =
    typeof window !== 'undefined'
      ? `${window.location.origin}/api/diagnostics/client-error/`
      : ''

  opts = {
    ingestUrl: options.ingestUrl ?? defaultIngest,
    maxBufferSize: options.maxBufferSize ?? 50,
    flushDebounceMs: options.flushDebounceMs ?? 2000,
    buildSha: options.buildSha,
    getAuthHeaders: options.getAuthHeaders,
    getOrganisationId: options.getOrganisationId,
    disabled: false,
  }

  sessionKey = _resolveSessionKey()
  _restoreBufferFromStorage()
  originalFetch = installFetchInstrumentation({
    getIngestUrl: () => opts.ingestUrl,
    parseServerTiming: _parseServerTiming,
    onFetchTiming: _emitFetchTiming,
    shouldSkipDiagnostic: _shouldSkipFetchDiagnostic,
    onResponseError: _recordFetchResponseError,
    onNetworkError: _recordFetchNetworkError,
  })
  installClientErrorGlobalListeners({
    record: _recordAutomaticDiagnostic,
    flush: _flushNow,
  })
  installClientDiagnosticsKeyboardShortcut(() => buffer)

  installed = true

  // XHR instrumentation — axios's browser adapter is XMLHttpRequest, so the
  // fetch patch above is blind to most hubs' API traffic (QDrive, CRM, the
  // legacy dashboard all use axios). Without this, a user-visible failed
  // request (e.g. an upload 403) leaves the console at "0 events"
  // (BUG-2026-07-07-DIAGNOSTICS-XHR-BLIND). Lazy import keeps it off the
  // critical path; hooks decouple it from this module's internals.
  try {
    void import('./xhrDiagnostics').then((m) => {
      m.installXhrDiagnostics({
        record: (partial) => recordDiagnosticEvent(partial),
        shouldSkip: _shouldSkipFetchDiagnostic,
        getIngestUrl: () => opts.ingestUrl,
      })
    })
  } catch {
    /* XHR capture is best-effort; fetch capture still works without it */
  }

  // Right-click console (2026-07 client ask) — lazy import keeps the
  // menu/overlay code out of this module's critical path.
  if (!options.disableContextMenu) {
    try {
      void import('./consoleContextMenu').then((m) => m.installConsoleContextMenu())
    } catch {
      /* menu is best-effort; capture still works without it */
    }
  }

  // Boot the perf reporter with the SAME options so every tenant that
  // installs error diagnostics also gets render/fetch/web-vital timing —
  // universally, with zero per-app wiring. Lazy import keeps the two
  // modules decoupled and the error reporter usable on its own.
  try {
    void import('./clientPerfReporter').then((m) => {
      m.installClientPerfReporter({
        buildSha: options.buildSha,
        getAuthHeaders: options.getAuthHeaders,
        getOrganisationId: options.getOrganisationId,
      })
    })
  } catch {
    /* perf telemetry is best-effort; never block error diagnostics */
  }

  // OPT-IN PostHog: inert unless a key was provided. Lazy import so the
  // adapter (and any third-party code) is never loaded for the common
  // no-key case. Forwards business KPIs to PostHog in addition to 0x3admin.
  if (options.posthogApiKey) {
    try {
      void import('./posthog').then((m) => {
        m.installPostHog({
          apiKey: options.posthogApiKey,
          apiHost: options.posthogApiHost,
        })
      })
    } catch {
      /* product analytics is best-effort; never block diagnostics */
    }
  }
}

/**
 * Manually record an event (e.g. from a React error boundary).
 * Same shape as the auto-captured events.
 */
export function recordDiagnosticEvent(partial: Partial<DiagnosticEvent> & {
  event_type: DiagnosticEventType
  message: string
}): void {
  if (!installed) return
  _enqueue({
    id: _uuid(),
    ts: new Date().toISOString(),
    stack: '',
    route: _currentRoute(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    online_at_capture: typeof navigator !== 'undefined' ? navigator.onLine : true,
    build_sha: opts.buildSha,
    session_key: sessionKey,
    ...partial,
  })
}

/** Get the current ring buffer. Used by the viewer panel + tests. */
export function getDiagnosticBuffer(): readonly DiagnosticEvent[] {
  return buffer
}

/** Force-flush the buffer to the ingest endpoint. Returns the number
 *  of events sent (which may be 0 if the buffer is empty). */
export async function flushDiagnostics(): Promise<number> {
  if (buffer.length === 0) return 0
  return _flushNow()
}

/** Clear the buffer (does NOT delete events already POSTed). */
export function clearDiagnosticBuffer(): void {
  buffer = []
  _persistBuffer()
}

// --------- Internals --------------------------------------------------

function _resolveSessionKey(): string {
  try {
    let key = sessionStorage.getItem(SESSION_KEY_STORAGE_KEY)
    if (!key) {
      key = _uuid()
      sessionStorage.setItem(SESSION_KEY_STORAGE_KEY, key)
    }
    return key
  } catch {
    return _uuid()
  }
}

function _uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function _currentRoute(): string {
  if (typeof window === 'undefined') return ''
  return `${window.location.pathname}${window.location.hash}`
}

function _restoreBufferFromStorage(): void {
  try {
    const raw = localStorage.getItem(RING_BUFFER_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        buffer = parsed.slice(-opts.maxBufferSize)
        // Try to flush any leftover from a previous session that
        // crashed before the debounced flush could fire.
        _scheduleFlush()
      }
    }
  } catch {
    /* ignore */
  }
}

function _persistBuffer(): void {
  try {
    localStorage.setItem(
      RING_BUFFER_STORAGE_KEY,
      JSON.stringify(buffer.slice(-opts.maxBufferSize)),
    )
  } catch {
    /* localStorage quota / disabled — accept the loss */
  }
}

function _recordAutomaticDiagnostic(diagnostic: AutomaticDiagnostic): void {
  recordDiagnosticEvent(diagnostic)
}

function _enqueue(event: DiagnosticEvent): void {
  buffer.push(event)
  if (buffer.length > opts.maxBufferSize) {
    buffer.splice(0, buffer.length - opts.maxBufferSize)
  }
  _persistBuffer()
  _scheduleFlush()
}

function _scheduleFlush(): void {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void _flushNow()
  }, opts.flushDebounceMs)
}

async function _flushNow(extra?: { keepalive?: boolean }): Promise<number> {
  if (!opts.ingestUrl) return 0
  if (buffer.length === 0) return 0
  if (typeof window === 'undefined') return 0
  if (Date.now() < ingestPausedUntil) return 0

  const batch = buffer.slice(-20) // BE ingests up to 20 / request
  // Compute auth header eagerly — `keepalive` POSTs can't await.
  let authHeaders: Record<string, string> = {}
  if (opts.getAuthHeaders) {
    try {
      const v = await opts.getAuthHeaders()
      if (v && typeof v === 'object') authHeaders = v
    } catch {
      /* unauthenticated POST is fine */
    }
  }

  let organisationId: number | string | null | undefined
  if (opts.getOrganisationId) {
    try {
      organisationId = opts.getOrganisationId()
    } catch {
      /* ignore */
    }
  }

  const payload = {
    organisation_id: organisationId ?? undefined,
    events: batch,
  }

  // Use the ORIGINAL fetch (not our patched one) so we don't
  // re-enter ourselves and create an infinite ingest loop on a
  // backend outage.
  const send = originalFetch ?? window.fetch.bind(window)

  try {
    const res = await send(opts.ingestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify(payload),
      credentials: 'include',
      keepalive: extra?.keepalive ?? false,
    } as RequestInit)
    if (res.ok || res.status === 202) {
      // Trim the events we just sent off the buffer. Anything
      // newer than `batch` (added between slice() and now) is
      // preserved and will flush on the next debounce.
      const sentIds = new Set(batch.map((e) => e.id))
      buffer = buffer.filter((e) => !sentIds.has(e.id))
      _persistBuffer()
      return batch.length
    }
    if (res.status === 429) {
      ingestPausedUntil = Date.now() + 120_000
    }
    return 0
  } catch {
    // Ingest failure (backend down, offline, blocked) — keep events
    // in the buffer so the next page-load can retry from
    // _restoreBufferFromStorage.
    return 0
  }
}
